export const ROLES = ["superadmin", "owner", "sales", "operations", "viewer", "reception"] as const;
export type Role = (typeof ROLES)[number];

/**
 * The original users.role check cannot store reception or superadmin, so that column keeps
 * viewer for reception and owner for superadmin. The real role lives in staff_role.
 */
export function storedRole(role: Role): { role: Exclude<Role, "reception" | "superadmin">; staff_role: Role } {
  return { role: role === "reception" ? "viewer" : role === "superadmin" ? "owner" : role, staff_role: role };
}

/** Owner-level decisions (approvals, honouring an old rate) are open to owner and superadmin. */
export function isOwnerLevel(role: string | null | undefined): boolean {
  return role === "owner" || role === "superadmin";
}

export function staffRoleOf(staffRole: string | null | undefined, legacy: string | null | undefined): Role {
  const value = (staffRole ?? "").trim() || (legacy ?? "").trim();
  if ((ROLES as readonly string[]).includes(value)) return value as Role;
  return "viewer";
}

export const SALES_STAGES = [
  "new_inquiry",
  "needs_information",
  "qualified",
  "proposal_preparation",
  "proposal_sent",
  "negotiation",
  "accepted_pending_booking",
  "confirmed",
  "completed",
  "lost",
  "cancelled",
] as const;
export type SalesStage = (typeof SALES_STAGES)[number];

export const STAGE_LABELS: Record<SalesStage, string> = {
  new_inquiry: "Νέο αίτημα",
  needs_information: "Λείπουν στοιχεία",
  qualified: "Προκρίθηκε",
  proposal_preparation: "Ετοιμασία πρότασης",
  proposal_sent: "Η πρόταση στάλθηκε",
  negotiation: "Διαπραγμάτευση",
  accepted_pending_booking: "Αποδοχή, εκκρεμεί κράτηση",
  confirmed: "Επιβεβαιωμένο",
  completed: "Ολοκληρώθηκε",
  lost: "Χάθηκε",
  cancelled: "Ακυρώθηκε",
};

/**
 * Studio configuration: Χώροι, Τιμές, Μεταβλητές, Ροή, Εισαγωγή, Κατάλογος, Ρυθμίσεις.
 * Only the superadmin role holds these. The owner wildcard does not cover them, and
 * permissions_json cannot grant them, so a role change is the only way in.
 */
export const SUPERADMIN_PERMISSIONS = [
  "config.read",
  "settings.write",
  "users.manage",
  "rates.write",
  "rates.publish",
  "rules.publish",
  "custom_fields.write",
  "imports.write",
] as const;
const SUPERADMIN_ONLY = new Set<string>(SUPERADMIN_PERMISSIONS);

const ROLE_PERMISSIONS: Record<Role, readonly string[]> = {
  superadmin: ["*", ...SUPERADMIN_PERMISSIONS],
  owner: ["*"],
  sales: [
    "events.read",
    "events.write",
    "inquiries.read",
    "inquiries.write",
    "proposals.read",
    "proposals.write",
    "venues.read",
    "rates.read",
    "catalogue.read",
    "financials.read",
    "tasks.write",
    "custom_fields.read",
    "availability.read",
  ],
  operations: [
    "events.read",
    "venues.read",
    "availability.read",
    "availability.write",
    "holds.write",
    "tasks.write",
    "catalogue.read",
    "inquiries.read",
  ],
  viewer: ["events.read", "venues.read", "proposals.read", "availability.read", "catalogue.read"],
  reception: ["events.read", "events.write", "inquiries.read", "venues.read"],
};

export function grantedPermissions(role: Role, extra: string[] | null | undefined): Set<string> {
  const granted = new Set(ROLE_PERMISSIONS[role] ?? []);
  for (const entry of extra ?? []) {
    if (SUPERADMIN_ONLY.has(entry.replace(/^-/, ""))) continue;
    if (entry.startsWith("-")) granted.delete(entry.slice(1));
    else granted.add(entry);
  }
  return granted;
}

export function hasPermission(role: Role, extra: string[] | null | undefined, permission: string): boolean {
  if (SUPERADMIN_ONLY.has(permission)) return role === "superadmin";
  const granted = grantedPermissions(role, extra);
  return granted.has("*") || granted.has(permission);
}

export function canReadFinancials(role: Role, extra: string[] | null | undefined): boolean {
  return hasPermission(role, extra, "financials.read");
}
